// =====================================================================
// Tokens visuais compartilhados da Home (paleta + estilo dos cards)
// =====================================================================
// Extraídos do Figma do Marketing. Ficavam embutidos no Home.js; movidos para cá
// para o Dashboard de Operação (e futuros blocos da Home) reaproveitarem a MESMA
// paleta sem duplicar. Estilos inline (convenção do projeto — não há Tailwind).

import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, ArrowDown, Minus, Loader2, ChevronDown } from 'lucide-react';

export const COR = {
    paginaFundo: '#f2f2f2',        // fundo da área de conteúdo
    barraFundo: '#f7f6f6',         // barra superior
    buscaFundo: '#ececec',         // caixa de busca
    branco: '#ffffff',
    vermelhoEscuro: '#990605',     // ícones e detalhes (cor primária do portal)
    amarelo: '#FDBB20',
    amareloFundo: '#FEF3D4',       // elipse amarela clara (atalhos amarelos)
    iconeFundo: '#FFF2F2',         // elipse clara atrás dos ícones vermelhos
    texto: '#1a1a1a',
    subtitulo: '#c7c2c2',          // subtítulos dos cards
    creme: '#fef7e6',              // caixa de destaque interna
    bordaSuave: 'rgba(199,194,194,0.45)',
    rodape: '#a1a1a1',
};
export const FONTE = "'Raleway', sans-serif";
// Fonte do SISTEMA (Segoe UI no Windows, San Francisco no Mac…): traz algarismos
// tabulares de verdade — as casas batem coluna a coluna sem o ar de "máquina de
// escrever" da monospace. É a mesma formatação da Consulta de Fita (Financeiro).
// A Raleway NÃO tem tabular-nums (verificado por render).
//
// Os DASHBOARDS (Operação e RH) inteiros usam esta família (Julia, 2026-07-17):
// antes só os VALORES fugiam da Raleway e o texto ficava nela, o que colocava dois
// desenhos de dígito na mesma tela — o "15" do título "Nota — Primeiros 15 dias"
// saía diferente do 15 de qualquer tabela. Como número é o assunto do dashboard, é
// o TEXTO que vai até ele, não o contrário. O resto do portal (menus, botões,
// formulários, agenda) segue na Raleway do Figma do Marketing — por isso a troca é
// aplicada na RAIZ dos dois dashboards, não no `body`.
export const FONTE_DADOS = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
// Aplicar como `...NUM` em valor/célula FORA dos dashboards (a Home usa). Dentro
// deles é redundante: a raiz já herda família + tabular para tudo.
export const NUM = {
    fontFamily: FONTE_DADOS,
    fontVariantNumeric: 'tabular-nums',
};
// Raiz dos dashboards: família + algarismos tabulares para TODO o conteúdo abaixo
// (`font-variant-numeric` é herdado). Controles de formulário — <button>, <select>
// — NÃO herdam fonte por padrão: esses continuam pedindo a família na mão.
export const raizDashboard = { fontFamily: FONTE_DADOS, fontVariantNumeric: 'tabular-nums' };
export const SOMBRA = '0 4px 8px rgba(0,0,0,0.12)';
export const RAIO = '10px';

// =====================================================================
// Formatadores pt-BR compartilhados da Home
// =====================================================================
// Antes estavam duplicados dentro de DashboardOperacao.js e DashboardRH.js.
// Centralizados aqui para os dois (e futuros blocos) usarem os MESMOS.
// Regra geral: vírgula no decimal e ponto no milhar (toLocaleString('pt-BR')).

// Número cheio (inteiro com separador de milhar): 145230 → "145.230".
export const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR'));
// Número com 1 casa decimal: 12.5 → "12,5".
export const fmt1 = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
// Dinheiro cheio com 2 casas: 3456789.1 → "R$ 3.456.789,10".
export const fmtMoeda2 = (n) => (n == null ? '—' : `R$ ${Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Abrevia magnitudes grandes com SUFIXO curto (pedido da Julia, 2026-07-15 — os
// cards ficavam largos com "milhões"/"mil" por extenso):
//   ≥ 1 milhão → "3,46 Mi"
//   ≥ 1 mil    → "845,20 Mil"
//   abaixo disso → valor cheio (dinheiro com 2 casas; contagem inteira).
// `prefixo` entra colado à frente (ex.: 'R$ ' para dinheiro; '' para contagem).
// Motivo: os cards de valor alto da Home (Venda, Projeção, Orçamento, GC) não
// "estouram" mais com R$ 3.456.789,12 — mostram "R$ 3,46 Mi".
function abreviaMagnitude(n, prefixo) {
    if (n == null) return '—';
    const abs = Math.abs(n);
    const dec2 = (x) => x.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (abs >= 1e6) return `${prefixo}${dec2(n / 1e6)} Mi`;
    if (abs >= 1e3) return `${prefixo}${dec2(n / 1e3)} Mil`;
    // Abaixo de mil: dinheiro mantém 2 casas (R$ 32,50); contagem fica inteira (850).
    return prefixo ? `${prefixo}${dec2(n)}` : Number(n).toLocaleString('pt-BR');
}
// Dinheiro compacto: "R$ 3,46 Mi" · "R$ 845,20 Mil" · "R$ 32,50".
export const fmtMoedaCompacta = (n) => abreviaMagnitude(n, 'R$ ');
// Contagem compacta (sem R$): "1,45 Mi" · "145,23 Mil" · "850".
export const fmtNumCompacto = (n) => abreviaMagnitude(n, '');

// =====================================================================
// SetaChip — chip de tendência compartilhado (Operação e RH)
// =====================================================================
// A SETA é a direção real (▲ subiu / ▼ caiu / – estável); a COR diz se é bom
// (verde) ou ruim (vermelho). `bom` é decidido no call site, respeitando métricas
// em que "menor é melhor" (ex.: absenteísmo — acima do mercado é ▲ vermelho).
export function SetaChip({ delta, bom, texto }) {
    if (delta == null) return null;
    const estavel = delta === 0;
    const subiu = delta > 0;
    const cor = estavel ? '#6b7280' : (bom ? '#16a34a' : '#dc2626');
    const bg = estavel ? '#f1f5f9' : (bom ? '#dcfce7' : '#fee2e2');
    const Icone = estavel ? Minus : (subiu ? ArrowUp : ArrowDown);
    return (
        <span style={{ ...NUM, display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 700, color: cor, backgroundColor: bg, borderRadius: '6px', padding: '2px 7px' }}>
            <Icone size={12} />{texto}
        </span>
    );
}

// =====================================================================
// OverlayCarregando — véu de "aguarde" sobre um bloco da Home
// =====================================================================
// As queries do Dashboard (vendas/avaliações/RH) demoram alguns segundos. Antes,
// enquanto elas não voltavam, os cards ficavam mostrando "—" e os filtros
// continuavam clicáveis — o usuário mexia no período achando que já era o dado
// final e disparava uma nova rodada de queries por cima da que estava rodando.
// Este overlay cobre o conteúdo enquanto carrega; quem chama também desabilita os
// filtros (o overlay sozinho não protege o cabeçalho, que fica fora dele).
//
// Ancorado no TOPO do bloco (não centralizado): o dashboard é mais alto que a
// tela, e um spinner no centro geométrico cairia abaixo da dobra — o usuário
// veria o conteúdo apagado sem entender o porquê.
export function OverlayCarregando({ texto = 'Carregando dados…' }) {
    return (
        <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 20,
            // Véu na cor do fundo da página — apaga o conteúdo sem escurecer a tela.
            backgroundColor: 'rgba(242,242,242,0.6)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            paddingTop: '70px',
        }}>
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                backgroundColor: COR.branco, borderRadius: RAIO, boxShadow: SOMBRA,
                padding: '13px 20px', fontFamily: FONTE, fontSize: '14px',
                fontWeight: 700, color: COR.texto,
            }}>
                <Loader2 size={18} color={COR.vermelhoEscuro} className="animate-spin" />
                {texto}
            </div>
        </div>
    );
}

// Estilo do conteúdo enquanto o overlay está por cima: esmaecido e inerte (o
// `pointerEvents: none` impede clique em link/botão através do véu).
export const conteudoCarregando = (carregando) => ({
    opacity: carregando ? 0.4 : 1,
    pointerEvents: carregando ? 'none' : 'auto',
    transition: 'opacity .2s ease',
});

// Cabeçalho de painel (ícone + texto) — usado pelos cards da Home.
// Morava dentro do Home.js e era passado por prop para a AgendaCorporativa. Veio
// para cá quando a agenda passou a abrir também pelo ícone do cabeçalho (fora da
// Home): quem monta a agenda de outra tela não tem como receber essa prop do Home.
export function TituloPainel({ icone, texto }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {icone}
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COR.texto }}>{texto}</h3>
        </div>
    );
}

// =====================================================================
// TituloBloco — título de bloco/gráfico dos dashboards (Operação e RH)
// =====================================================================
// Pedido da Julia (2026-07-17): TODO título de gráfico/bloco das duas abas com a
// MESMA formatação — negrito, caixa alta e ícone na frente. Antes cada bloco
// escrevia o seu: 16px preto (Operação), 15px preto sem ícone (Real × Ideal),
// 13px cinza (Experiência/Aniversariantes/Produtividade) e 12px cinza (Notas).
//
// O TEXTO é sempre preto — o cinza de subtítulo (#c7c2c2) que os cards de RH
// usavam quase não se lê sobre o branco. Quem carrega a cor do bloco é o ÍCONE,
// via `cor` (é o que diferencia um card do outro sem apagar o título).
// `acao` = canto direito do cabeçalho (abas do ranking, toggle, badge de NPS…).
export const tituloBloco = {
    fontSize: '13px', fontWeight: 700, color: COR.texto,
    letterSpacing: '.04em', textTransform: 'uppercase',
};
export function TituloBloco({ icone, texto, cor, acao }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: cor || COR.vermelhoEscuro }}>
            {icone}
            {/* `flex: 1` empurra a `acao` para a direita do cabeçalho. */}
            <span style={{ ...tituloBloco, flex: 1 }}>{texto}</span>
            {acao}
        </div>
    );
}

// Cartão branco base (todos os painéis herdam daqui).
export const cartao = {
    backgroundColor: COR.branco,
    borderRadius: RAIO,
    boxShadow: SOMBRA,
    position: 'relative',
};

// =====================================================================
// Tokens de tamanho/tipografia dos cards de indicador (compartilhados)
// =====================================================================
// Pedido da Julia (2026-07-15): cards MENORES e com a MESMA formatação de texto
// nas duas abas (Operação e RH). Antes cada card definia seu próprio tamanho —
// os de venda/RH eram 132px/26px, o de avaliação 120px/24px e o de nota 30px.
// Centralizado aqui para as duas abas herdarem exatamente o mesmo. A COR do valor
// fica por conta de cada card (uns são vermelhos, outros escuros).
// Enxugados mais um passo em 2026-07-17 (Julia) — os números entre parênteses são
// a medida anterior. Quem mexer aqui mexe nos DOIS dashboards de uma vez.
export const CARD_PAD = '12px 14px';       // padding interno (era 14px 16px)
export const CARD_MIN_H = '96px';           // altura mínima (era 108px)
export const CARD_GAP = '6px';              // espaçamento vertical interno (era 8px)
// Rótulo (título) do card: caixa alta curta em negrito.
export const rotuloCard = { fontSize: '11px', fontWeight: 700, color: COR.texto, letterSpacing: '.02em' };
// Número grande do card (sem cor — cada card escolhe a sua). Fonte tabular (NUM).
export const valorCard = { fontSize: '19px', fontWeight: 800, lineHeight: 1.1, ...NUM };
// Rodapé de contexto do card ("sem orçado no período", "Sem avaliação"). Estava
// escrito à mão em 12px em cada card — centralizado aqui no mesmo passo para não
// voltar a divergir.
export const rodapeCard = { fontSize: '11px', color: '#666', fontWeight: 600 };

// =====================================================================
// LinhaMercado — a linha "Mercado: …" dos cards (Operação e RH)
// =====================================================================
// Mostra como o MERCADO (a rede, fora as lojas em foco) se moveu no período
// escolhido contra o MESMO período do ano anterior.
//
// Julia, 2026-07-21: esta linha trazia o VALOR do mercado (R$ 890 mil, 3,2%,
// 4,1/5). Ao lado de um card cujo destaque é o CRESCIMENTO da loja, o valor não
// respondia a pergunta que o gestor faz — "eu cresci mais ou menos que o
// mercado?" —, porque os dois números estavam em réguas diferentes. Agora o
// mercado vem na mesma régua do chip da loja e a comparação é leitura direta.
//
// A unidade é SEMPRE variação relativa % (Julia, 2026-07-21), em todos os cards —
// venda, absenteísmo, notas de pesquisa e avaliações. Uma régua só: o gestor lê a
// linha do mesmo jeito em qualquer card, sem parar para descobrir o que aquela
// unidade significa ali.
//
// ⚠️ A leitura: é a variação DA MEDIDA, não a distância até ela. Num card cuja
// medida já é um percentual (absenteísmo, avaliações), "▲ 13%" quer dizer que a
// taxa do mercado subiu 13% do próprio tamanho — 3,0% virou 3,4%, e NÃO que subiu
// 13 pontos. É a mesma conta do chip da loja logo acima, que é o ponto: os dois
// números do card ficam comparáveis entre si.
//
// `bomSubir` = a direção boa da métrica, para a cor da seta. Vale a pena reparar:
// aqui a cor qualifica o movimento do MERCADO, não o desempenho da loja. Mercado
// piorando aparece em vermelho mesmo sendo, para a loja, uma barra mais fácil.
export function LinhaMercado({ variacao, bomSubir = true }) {
    if (variacao == null) return null;
    const num = Math.abs(variacao).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return (
        <div style={{ ...rodapeCard, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            Mercado:
            <SetaChip
                delta={variacao}
                bom={variacao === 0 ? true : (variacao > 0) === bomSubir}
                texto={`${num}% vs ano ant.`}
            />
        </div>
    );
}

// =====================================================================
// Combobox de LOJAS (multi-seleção) — compartilhado
// =====================================================================
// Vazio = rede toda; marque uma ou mais. Fecha ao clicar fora.
//
// ⚠️ Veio do `DashboardOperacao.js` em 11/09/2026, ao ganhar o SEGUNDO consumidor
//    (o filtro de pendências da aba Escritório). Mora aqui, e não lá, porque um
//    componente importado da tela do vizinho amarra duas telas que não têm
//    relação — e porque a cópia era a alternativa, que diverge sem avisar.
//
// ⚠️ `desabilitado` fecha a lista além de travar o botão: aberta e inerte, ela
//    parece quebrada.
const selectTravado = { backgroundColor: '#f0eeee', color: '#9a9a9a', cursor: 'wait' };

export function MultiSelectLojas({ lojas, selecionados, onChange, selectStyle, desabilitado }) {
    const [aberto, setAberto] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, []);
    useEffect(() => { if (desabilitado) setAberto(false); }, [desabilitado]);
    const toggle = (l) => onChange(
        selecionados.includes(l) ? selecionados.filter((x) => x !== l) : [...selecionados, l].sort()
    );
    const resumo = selecionados.length === 0 ? 'Rede (todas as lojas)'
        : selecionados.length === 1 ? selecionados[0]
            : `${selecionados.length} lojas`;
    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setAberto((a) => !a)} disabled={desabilitado}
                style={{ ...selectStyle, ...(desabilitado ? selectTravado : null), minWidth: '175px', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                title="Lojas em foco (vazio = rede toda; marque uma ou mais e clique em Aplicar)">
                {resumo} <ChevronDown size={14} />
            </button>
            {aberto && (
                <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', right: 0, backgroundColor: '#fff', border: `1px solid ${COR.bordaSuave}`, borderRadius: RAIO, boxShadow: SOMBRA, padding: '10px', width: '230px' }}>
                    <button type="button" onClick={() => onChange([])}
                        style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', fontFamily: FONTE_DADOS, fontSize: '12px', fontWeight: 700, color: COR.vermelhoEscuro, padding: '2px 4px 8px' }}>
                        Limpar (rede toda)
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: '260px', overflowY: 'auto' }}>
                        {lojas.map((l) => {
                            const on = selecionados.includes(l);
                            return (
                                <button key={l} type="button" onClick={() => toggle(l)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', border: `1px solid ${on ? COR.vermelhoEscuro : COR.bordaSuave}`, backgroundColor: on ? COR.vermelhoEscuro : '#fff', color: on ? '#fff' : COR.texto, borderRadius: '6px', padding: '6px 9px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: FONTE_DADOS }}>
                                    <span style={{ width: '13px', flexShrink: 0 }}>{on ? '✓' : ''}</span>{l}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

